// Lens filter — which categories to show. Replaces the old single-mode
// toggle. Defaults to both lenses active.
//
// Backward-compat: getMode() still returns a RiverCategory for the
// drawer/map color choice — picks whatever's currently active, prefers
// whitewater when both are on. Most call sites use getLenses() instead.

import type { RiverCategory } from '../../functions/_lib/types';

const KEY = 'frozen-fly:lenses-v1';

export type LensSet = {
  whitewater: boolean;
  fly_fishing: boolean;
};

type LensListener = (lenses: LensSet) => void;

function loadInitial(): LensSet {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const v = JSON.parse(raw) as Partial<LensSet>;
      const lenses: LensSet = {
        whitewater: v.whitewater !== false,
        fly_fishing: v.fly_fishing !== false,
      };
      if (!lenses.whitewater && !lenses.fly_fishing) {
        // Don't let the user save an empty selection — fall back to both.
        return { whitewater: true, fly_fishing: true };
      }
      return lenses;
    }
  } catch { /* ignore */ }
  return { whitewater: true, fly_fishing: true };
}

let lenses: LensSet = loadInitial();
const lensListeners = new Set<LensListener>();

export function getLenses(): LensSet {
  return { ...lenses };
}

export function setLenses(next: LensSet): void {
  // Refuse empty set — bounce back to both-on.
  const safe: LensSet = next.whitewater || next.fly_fishing ? next : { whitewater: true, fly_fishing: true };
  if (safe.whitewater === lenses.whitewater && safe.fly_fishing === lenses.fly_fishing) return;
  lenses = safe;
  try { localStorage.setItem(KEY, JSON.stringify(lenses)); } catch { /* ignore */ }
  for (const l of lensListeners) l({ ...lenses });
}

export function toggleLens(cat: RiverCategory): void {
  setLenses({ ...lenses, [cat]: !lenses[cat] });
}

export function onLensChange(fn: LensListener): () => void {
  lensListeners.add(fn);
  return () => lensListeners.delete(fn);
}

/** Returns true if the given category is one of the currently-shown lenses. */
export function lensActive(cat: RiverCategory): boolean {
  return lenses[cat];
}

// ── Back-compat shim for components that still expect a single "mode" ──
// Used in places that need a single color/category context: the drawer
// (which shows all applicable narratives anyway), the map (color picks
// the best lens per river), and the hero gradient. Prefer whitewater when
// both lenses are on.

export function getMode(): RiverCategory {
  if (lenses.whitewater) return 'whitewater';
  return 'fly_fishing';
}

export function setMode(cat: RiverCategory): void {
  // Legacy callers — flip to a single-lens view.
  setLenses({ whitewater: cat === 'whitewater', fly_fishing: cat === 'fly_fishing' });
}

export function onModeChange(fn: (cat: RiverCategory) => void): () => void {
  return onLensChange(() => fn(getMode()));
}
